import { useEffect, useState, type CSSProperties } from "react";
import type { Game } from "@engine/core/Game";
import { useKeybindingsStore } from "@engine/state";

interface Props {
  game?: Game | undefined;
  open: boolean;
  onClose: () => void;
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1500,
};

const panelStyle: CSSProperties = {
  width: 460,
  maxHeight: "80vh",
  background: "#0f1218",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 8,
  color: "#e6e8ee",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 13,
  padding: 16,
  boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "6px 0",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const codeBtn: CSSProperties = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "#e6e8ee",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: 12,
  fontFamily: "ui-monospace, monospace",
  cursor: "pointer",
};

/**
 * Modal that lists every action and its bound codes. Click a code to capture
 * a new key. Esc closes.
 *
 *  • Persists to `useKeybindingsStore` (zustand + localStorage).
 *  • Applies live to `game.actions`.
 */
export function RebindingPanel({ game, open, onClose }: Props) {
  const json = useKeybindingsStore((s) => s.json);
  const setBinding = useKeybindingsStore((s) => s.setBinding);
  const reset = useKeybindingsStore((s) => s.reset);
  const [capturing, setCapturing] = useState<{ action: string; index: number } | null>(null);

  // Apply the persisted bindings to the live ActionMap whenever they change.
  useEffect(() => {
    if (!game) return;
    game.actions.loadJSON(json);
  }, [game, json]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Escape" && !capturing) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, capturing]);

  // Capture mode: next non-Escape keydown becomes the new binding.
  useEffect(() => {
    if (!capturing) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === "Escape") {
        setCapturing(null);
        return;
      }
      const codes = [...(json.bindings[capturing.action] ?? [])];
      codes[capturing.index] = e.code;
      setBinding(capturing.action, codes);
      setCapturing(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [capturing, json.bindings, setBinding]);

  if (!open) return null;

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <div style={panelStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <strong>Keybindings</strong>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={() => reset()} style={smallBtn}>reset</button>
            <button type="button" onClick={onClose} style={smallBtn}>close</button>
          </div>
        </div>
        <div style={{ overflowY: "auto" }}>
          {Object.entries(json.bindings).map(([action, codes]) => (
            <div key={action} style={rowStyle}>
              <span style={{ opacity: 0.85 }}>{action}</span>
              <span style={{ display: "flex", gap: 6 }}>
                {codes.map((c, i) => (
                  <button
                    key={`${action}-${i}`}
                    type="button"
                    onClick={() => setCapturing({ action, index: i })}
                    style={{
                      ...codeBtn,
                      background:
                        capturing?.action === action && capturing.index === i
                          ? "#2563eb"
                          : codeBtn.background,
                    }}
                  >
                    {capturing?.action === action && capturing.index === i ? "press a key…" : c}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setBinding(action, [...codes, "(unset)"])}
                  style={{ ...codeBtn, opacity: 0.6 }}
                  title="add binding"
                >
                  +
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const smallBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "#e6e8ee",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 12,
  cursor: "pointer",
};
